import { SharePointListClient, type ListItem } from '../SharePointListClient';

/**
 * 99.ABM_CuentaContable — GL accounts. Categoria_CC is a soft lookup (text)
 * to 99.ABM_Categorias.Categoria_CAT. The boolean "ImpactaStock" /
 * "NroSerie" flags are stored as 'SI' / 'NO' strings in the legacy list.
 */
export type CuentaContableFields = {
  Title?: string | null;
  CuentaContable_CC?: string | null;
  Categoria_CC?: string | null;
  ImpactaStock_CC?: 'SI' | 'NO' | string | null;
  NroSerie_CC?: 'SI' | 'NO' | string | null;
  Status_CC?: 'Activo' | 'Inactivo' | string | null;
  /**
   * Estado Financiero classification (AR-only today). The operator picks
   * only the EF SubItem (`EFSubItemId_CC` = ef_subitems.id); the EF Item is
   * derived from it server-side. `EFSubItem_CC` / `EFItem_CC` are read-only
   * display names.
   */
  EFSubItemId_CC?: string | null;
  EFSubItem_CC?: string | null;
  EFItem_CC?: string | null;
};

export type CuentaContableItem = ListItem<CuentaContableFields>;

export const cuentasContablesClient = new SharePointListClient<CuentaContableFields>(
  'ABM_CuentaContable',
);

export async function listAllCuentasContables(): Promise<CuentaContableItem[]> {
  return cuentasContablesClient.listItems({ top: 5000 });
}

export async function listActiveCuentasContables(): Promise<CuentaContableItem[]> {
  return cuentasContablesClient.listItems({
    filter: "fields/Status_CC eq 'Activo'",
    orderBy: 'fields/CuentaContable_CC asc',
  });
}

export type CuentaContablePayload = {
  cuentaContable: string;
  categoria?: string | null;
  impactaStock: boolean;
  nroSerie: boolean;
  /** ef_subitems.id as a string, or null to clear. AR-only in practice. */
  efSubItemId?: string | null;
};

function toBooleanString(b: boolean): 'SI' | 'NO' {
  return b ? 'SI' : 'NO';
}

export async function createCuentaContable(
  payload: CuentaContablePayload,
): Promise<CuentaContableItem> {
  return cuentasContablesClient.createItem({
    Title: '[sumar]',
    CuentaContable_CC: payload.cuentaContable.trim(),
    Categoria_CC: payload.categoria?.trim() || null,
    ImpactaStock_CC: toBooleanString(payload.impactaStock),
    NroSerie_CC: toBooleanString(payload.nroSerie),
    EFSubItemId_CC: payload.efSubItemId ?? null,
    Status_CC: 'Activo',
  });
}

export async function updateCuentaContable(
  id: string,
  payload: CuentaContablePayload,
): Promise<CuentaContableItem> {
  return cuentasContablesClient.updateItem(id, {
    CuentaContable_CC: payload.cuentaContable.trim(),
    Categoria_CC: payload.categoria?.trim() || null,
    ImpactaStock_CC: toBooleanString(payload.impactaStock),
    NroSerie_CC: toBooleanString(payload.nroSerie),
    EFSubItemId_CC: payload.efSubItemId ?? null,
  });
}

export async function softDeleteCuentaContable(id: string): Promise<CuentaContableItem> {
  return cuentasContablesClient.updateItem(id, { Status_CC: 'Inactivo' });
}
